<template>
    <div class="box-score">
        <div class="box-score__head">
            <div class="box-score__subtitle">Στατιστικά παικτών</div>
            <div class="box-toggle">
                <button
                    v-for="side in teams"
                    :key="side.key"
                    type="button"
                    class="box-toggle__btn"
                    :class="{ 'box-toggle__btn--active': selectedTeam === side.key }"
                    @click="selectedTeam = side.key"
                >
                    <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                    <span>{{ side.team.name }}</span>
                </button>
            </div>
        </div>

        <div class="box-table-wrap">
            <table class="box-table">
                <thead>
                    <tr>
                        <th
                            v-for="column in columns"
                            :key="column.key"
                            :class="[`box-col--${column.key}`, { 'box-col--sorted': sortKey === column.key }]"
                            @click="sortBy(column.key)"
                        >
                            <span class="box-th">
                                <info-tip :text="column.title" :underline="!!column.title" pass-tap>{{ column.label }}</info-tip>
                                <info-tip v-if="column.key === 'ts'" bottom max-width="380" content-class="ts-tooltip-surface">
                                    <v-icon x-small class="ts-info-icon" @click.stop>mdi-information-outline</v-icon>
                                    <template #content>
                                        <div class="ts-tooltip">
                                            <div class="font-weight-bold mb-1">Πραγματικό ποσοστό ευστοχίας (TS%)</div>
                                            <div class="mb-2">Πόντοι / (2 × (σουτ + 0,44 × βολές)). Μετράει μαζί δίποντα, τρίποντα και βολές.</div>
                                            <div class="ts-poor">Κάτω από 50%: χαμηλή απόδοση</div>
                                            <div class="ts-below-average">50% έως 55%: κάτω από τον μέσο όρο</div>
                                            <div class="ts-average">55% έως 57%: μέσος όρος</div>
                                            <div class="ts-very-good">57% έως 60%: πολύ καλή απόδοση</div>
                                            <div class="ts-elite">60% και πάνω: εξαιρετική</div>
                                        </div>
                                    </template>
                                </info-tip>
                                <v-icon v-if="sortKey === column.key" x-small class="box-sort-icon">
                                    {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                                </v-icon>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in sortedRows" :key="row.key" class="box-row" @click="goToPlayer(row.id)">
                        <td class="box-col--player">
                            <span class="box-player">
                                <img :src="row.image" alt="" @error="$event.target.src = defaultPlayerImg">
                                <span class="box-player__jersey">{{ row.jersey !== null ? `#${row.jersey}` : '' }}</span>
                                <span class="box-player__name">{{ row.name }}</span>
                                <span class="box-player__surname">{{ row.surname }}</span>
                            </span>
                        </td>
                        <td>{{ row.minutesLabel }}</td>
                        <td class="box-strong">{{ row.points }}</td>
                        <td v-for="shot in shotKeys" :key="shot">
                            <span class="box-shot">
                                {{ row[shot].made }}/{{ row[shot].attempts }}
                                <small>{{ row[shot].attempts ? `${row[shot].pct}%` : '—' }}</small>
                            </span>
                        </td>
                        <td :class="tsClass(row.ts)" class="box-strong">{{ row.tsAttempts ? `${row.ts}%` : '—' }}</td>
                        <td v-for="key in countKeys" :key="key">{{ row[key] }}</td>
                        <td class="box-strong">{{ row.pir }}</td>
                    </tr>
                    <tr v-if="!sortedRows.length">
                        <td :colspan="columns.length" class="box-empty">Δεν υπάρχουν ακόμα στατιστικά</td>
                    </tr>
                </tbody>
                <tfoot v-if="sortedRows.length">
                    <tr class="box-total">
                        <td class="box-col--player">Σύνολο ομάδας</td>
                        <td />
                        <td>{{ totals.points }}</td>
                        <td v-for="shot in shotKeys" :key="`total-${shot}`">
                            <span class="box-shot">
                                {{ totals[shot].made }}/{{ totals[shot].attempts }}
                                <small>{{ totals[shot].attempts ? `${totals[shot].pct}%` : '—' }}</small>
                            </span>
                        </td>
                        <td>{{ totals.tsAttempts ? `${totals.ts}%` : '—' }}</td>
                        <td v-for="key in countKeys" :key="`total-${key}`">{{ totals[key] }}</td>
                        <td>{{ totals.pir }}</td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <div class="box-legend">
            <span v-for="column in columns.filter(item => item.title)" :key="`legend-${column.key}`"><b>{{ column.label }}</b> {{ column.title }}</span>
        </div>
    </div>
</template>

<script>
    import { foulsOf, getStats, getPir, getPlayerImage, getJersey, hasPlayerStats, percentage, formatMinutes } from '@/utils/playerStats'

    const COUNT_KEYS = ['offensiveRebounds', 'secondChancePoints', 'defensiveRebounds', 'rebounds', 'assists', 'assistPoints', 'steals', 'pointsOffTurnovers', 'turnovers', 'blockedShots', 'fouls']
    const SHOTS = {
        two: ['twoPointsMade', 'twoPointAttempts'],
        three: ['threePointsMade', 'threePointAttempts'],
        free: ['freeThrowsMade', 'freeThrowAttempts']
    }

    const tsPercentage = (points, fieldGoalAttempts, freeThrowAttempts) => {
        const denominator = 2 * (fieldGoalAttempts + (0.44 * freeThrowAttempts))
        return denominator ? Math.round((points / denominator) * 100) : 0
    }

    // The box score of one team at a time, sortable by every column
    export default {
        props: {
            match: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                selectedTeam: 'team1',
                sortKey: 'points',
                sortDesc: true,
                shotKeys: Object.keys(SHOTS),
                countKeys: COUNT_KEYS,
                columns: [
                    { key: 'player', label: 'Παίκτης' },
                    { key: 'minutes', label: 'ΛΕΠ', title: 'Λεπτά συμμετοχής' },
                    { key: 'points', label: 'ΠΟΝ', title: 'Πόντοι' },
                    { key: 'two', label: '2Π', title: 'Δίποντα (εύστοχα/προσπάθειες)' },
                    { key: 'three', label: '3Π', title: 'Τρίποντα (εύστοχα/προσπάθειες)' },
                    { key: 'free', label: 'ΒΟΛ', title: 'Βολές (εύστοχες/προσπάθειες)' },
                    { key: 'ts', label: 'TS%', title: 'Πραγματικό ποσοστό ευστοχίας' },
                    { key: 'offensiveRebounds', label: 'ΕΡ', title: 'Επιθετικά ριμπάουντ' },
                    { key: 'secondChancePoints', label: 'Π2Ε', title: 'Πόντοι 2ης ευκαιρίας' },
                    { key: 'defensiveRebounds', label: 'ΑΡ', title: 'Αμυντικά ριμπάουντ' },
                    { key: 'rebounds', label: 'ΡΙΜ', title: 'Ριμπάουντ' },
                    { key: 'assists', label: 'ΑΣ', title: 'Ασίστ' },
                    { key: 'assistPoints', label: 'ΠΑΣ', title: 'Πόντοι από ασίστ' },
                    { key: 'steals', label: 'ΚΛ', title: 'Κλεψίματα' },
                    { key: 'pointsOffTurnovers', label: 'ΠΛΑ', title: 'Πόντοι από λάθη αντιπάλου' },
                    { key: 'turnovers', label: 'ΛΘ', title: 'Λάθη' },
                    { key: 'blockedShots', label: 'ΚΟ', title: 'Κοψίματα' },
                    { key: 'fouls', label: 'ΦΑ', title: 'Φάουλ' },
                    { key: 'pir', label: 'PIR', title: 'Συντελεστής απόδοσης' }
                ]
            }
        },
        computed: {
            teams() {
                return ['team1', 'team2'].map(key => ({ key, team: (this.match && this.match[key]) || {} }))
            },
            rows() {
                const team = (this.match && this.match[this.selectedTeam]) || {}
                const players = Array.isArray(team.players) ? team.players : []
                return players
                    .filter(player => player.active && hasPlayerStats(player))
                    .map((player, index) => {
                        const stats = getStats(player)
                        const row = {
                            key: player.id || index,
                            id: player.id,
                            name: `${player.name || ''} ${player.surname || ''}`.trim(),
                            surname: player.surname || player.name || '',
                            jersey: getJersey(player),
                            image: getPlayerImage(player, this.defaultPlayerImg),
                            minutes: Number(stats.playTimeInSecs) || 0,
                            minutesLabel: formatMinutes(stats.playTimeInSecs),
                            points: Number(stats.points) || 0,
                            pir: getPir(stats)
                        }
                        Object.entries(SHOTS).forEach(([key, [madeKey, attemptsKey]]) => {
                            const made = Number(stats[madeKey]) || 0
                            const attempts = Number(stats[attemptsKey]) || 0
                            row[key] = { made, attempts, pct: percentage(made, attempts) }
                        })
                        // the fouls: every foul, personal, technical and unsportsmanlike
                        COUNT_KEYS.forEach((key) => { row[key] = key === 'fouls' ? foulsOf(stats) : Number(stats[key]) || 0 })
                        row.tsAttempts = row.two.attempts + row.three.attempts + row.free.attempts
                        row.ts = tsPercentage(row.points, row.two.attempts + row.three.attempts, row.free.attempts)
                        return row
                    })
            },
            sortedRows() {
                const value = (row) => {
                    if (this.sortKey === 'player') return row.jersey === null ? Infinity : Number(row.jersey)
                    if (SHOTS[this.sortKey]) return row[this.sortKey].made * 1000 + row[this.sortKey].pct
                    return row[this.sortKey]
                }
                const direction = this.sortDesc ? -1 : 1
                return [...this.rows].sort((a, b) => ((value(a) - value(b)) * direction) || (b.points - a.points))
            },
            totals() {
                const totals = { points: 0, pir: 0 }
                COUNT_KEYS.forEach((key) => { totals[key] = 0 })
                Object.keys(SHOTS).forEach((key) => { totals[key] = { made: 0, attempts: 0 } })
                this.rows.forEach((row) => {
                    totals.points += row.points
                    totals.pir += row.pir
                    COUNT_KEYS.forEach((key) => { totals[key] += row[key] })
                    Object.keys(SHOTS).forEach((key) => {
                        totals[key].made += row[key].made
                        totals[key].attempts += row[key].attempts
                    })
                })
                Object.keys(SHOTS).forEach((key) => { totals[key].pct = percentage(totals[key].made, totals[key].attempts) })
                totals.tsAttempts = totals.two.attempts + totals.three.attempts + totals.free.attempts
                totals.ts = tsPercentage(totals.points, totals.two.attempts + totals.three.attempts, totals.free.attempts)
                return totals
            }
        },
        methods: {
            sortBy(key) {
                if (this.sortKey === key) {
                    this.sortDesc = !this.sortDesc
                    return
                }
                this.sortKey = key
                // names and jerseys read best ascending, numbers best first
                this.sortDesc = key !== 'player'
            },
            goToPlayer(id) {
                if (id) this.$router.push({ name: 'Player', params: { id } })
            },
            tsClass(ts) {
                if (ts < 50) return 'ts-poor'
                if (ts < 55) return 'ts-below-average'
                if (ts < 57) return 'ts-average'
                if (ts < 60) return 'ts-very-good'
                return 'ts-elite'
            }
        }
    }
</script>

<style scoped>
.box-score {
    padding: 16px 18px 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.box-score__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.box-score__subtitle {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.box-toggle {
    display: inline-flex;
    gap: 2px;
    max-width: 100%;
    padding: 3px;
    border-radius: 999px;
    background: #0d0d0f;
}

.box-toggle__btn {
    display: inline-flex;
    flex: 0 1 auto;
    align-items: center;
    gap: 6px;
    min-width: 0;
    padding: 5px 16px 5px 6px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.88rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease;
}

.box-toggle__btn span {
    overflow: hidden;
    text-overflow: ellipsis;
}

.box-toggle__btn:hover {
    color: #fff;
}

.box-toggle__btn--active {
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
}

.box-toggle__btn img {
    width: 26px;
    height: 26px;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

/* ---------- Table ---------- */
.box-table-wrap {
    overflow: auto;
    border: 1px solid rgba(0, 0, 0, 0.07);
    border-radius: 10px;
}

.box-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    color: #2b2b2e;
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
}

.box-table th,
.box-table td {
    padding: 8px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    text-align: center;
    white-space: nowrap;
}

.box-table th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #0d0d0f;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    cursor: pointer;
    user-select: none;
}

.box-table th.box-col--sorted {
    color: #ff8a3d;
}

.box-th {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.box-th .v-icon {
    color: inherit;
}

/* the player column stays put while the numbers scroll */
.box-col--player {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    text-align: left !important;
    box-shadow: 1px 0 0 rgba(0, 0, 0, 0.06);
}

.box-table th.box-col--player {
    z-index: 3;
    background: #0d0d0f;
}

.box-row {
    cursor: pointer;
}

.box-player {
    display: flex;
    align-items: center;
    gap: 8px;
}

.box-player img {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f1f1f3;
    object-fit: cover;
    object-position: top;
}

.box-player__jersey {
    min-width: 28px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.78rem;
    font-weight: 700;
}

.box-player__name {
    font-weight: 700;
}

.box-player__surname {
    display: none;
    font-weight: 700;
}

.box-strong {
    font-weight: 800;
}

.box-shot {
    display: inline-flex;
    flex-direction: column;
    line-height: 1.1;
}

.box-shot small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.68rem;
    font-weight: 700;
}

.box-total td {
    border-bottom: 0;
    background: #f4f4f6;
    font-weight: 800;
}

.box-row:hover td {
    background: #fff8f2;
}

.box-total .box-col--player {
    background: #f4f4f6;
}

.box-empty {
    padding: 24px !important;
    color: rgba(0, 0, 0, 0.45);
}

.box-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    padding-top: 10px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
}

.box-legend b {
    color: rgba(0, 0, 0, 0.7);
}

/* ---------- TS% ---------- */
.ts-info-icon {
    cursor: pointer;
}

.ts-tooltip {
    font-size: 13px;
    line-height: 1.4;
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.ts-poor {
    color: #d32f2f;
}

.ts-below-average {
    color: #ef6c00;
}

.ts-average {
    color: #f9a825;
}

.ts-very-good {
    color: #2e7d32;
}

.ts-elite {
    color: #1b5e20;
}

@media (max-width: 600px) {
    .box-score {
        padding: 14px 10px 10px;
    }

    .box-score__head {
        flex-direction: column;
        align-items: stretch;
    }

    .box-toggle__btn {
        flex: 1 1 0;
        justify-content: center;
    }

    /* the header stays visible while scrolling down a long roster */
    .box-table-wrap {
        max-height: 70vh;
    }

    .box-table th,
    .box-table td {
        padding: 7px 6px;
    }

    .box-player img {
        width: 26px;
        height: 26px;
    }

    /* on phones the fixed column keeps to the surname, so the numbers get the room */
    .box-player {
        gap: 5px;
    }

    .box-player__jersey {
        min-width: 0;
        font-size: 0.7rem;
    }

    .box-player__name {
        display: none;
    }

    .box-player__surname {
        display: inline;
        max-width: 92px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
